import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Currency } from '@dlap-ui/shared-forms/models/currency';
import { PAYMENTS_CONFIG } from '../payments.config';
import {
  FeeTypeEntry,
  PaymentApiPage,
  PaymentApiResponse,
  PaymentConfig,
} from '../models/payment.models';

/**
 * Talks to the public checkout/payment endpoints.
 *
 * (Adapted from ZIM-Portal's CheckoutService — configuration now comes from
 * the injected {@link PAYMENTS_CONFIG} instead of a runtime ConfigService.)
 *
 * Every gateway endpoint answers with a `{ success, code, data, … }` envelope;
 * each method here unwraps `data` so callers only see the payload.
 */
@Injectable({ providedIn: 'root' })
export class CheckoutService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(PAYMENTS_CONFIG);

  readonly baseUrl = this.config.apiUrl;
  readonly frontendUrl = this.config.frontendUrl;

  private readonly publicCheckoutUrl = this.baseUrl + 'public/payment';
  private readonly feePublicUrl = this.baseUrl + 'public/fee';

  generateMerchantReference(
    feeId: string,
    callbackUrl: string
  ): Observable<any> {
    const url = `${this.publicCheckoutUrl}/generate-merchant-reference/${feeId}`;
    const headers = new HttpHeaders()
      .set('feeId', feeId)
      .set('callbackUrl', callbackUrl);

    return this.http
      .post<PaymentApiResponse<any>>(url, {}, { headers })
      .pipe(map((res) => res?.data), catchError(this.handleError));
  }

  generateMerchantReferenceWithoutFee(request: any): Observable<any> {
    const url = `${this.publicCheckoutUrl}/generate-merchant-reference`;
    return this.http
      .post<PaymentApiResponse<any>>(url, request)
      .pipe(map((res) => res?.data), catchError(this.handleError));
  }

  getReference(reference: string): Observable<any> {
    const url = `${this.publicCheckoutUrl}/status/reference`;
    const params = new HttpParams().set('reference', reference);
    return this.http
      .get<PaymentApiResponse<any>>(url, { params })
      .pipe(map((res) => res?.data), catchError(this.handleError));
  }

  getPublicActiveFeeTypes(): Observable<FeeTypeEntry[]> {
    return this.http
      .get<PaymentApiResponse<FeeTypeEntry[]>>(`${this.feePublicUrl}/list-active`)
      .pipe(map((res) => res?.data ?? []));
  }

  prepareZimswitchCheckout(request: PaymentConfig): Observable<any> {
    return this.http
      .post<PaymentApiResponse<any>>(`${this.publicCheckoutUrl}/create-check-out`, request)
      .pipe(map((res) => res?.data));
  }

  getCurrencies(name = '', status = 'ACTIVE'): Observable<Currency[]> {
    const params = new HttpParams()
      .set('pageSize', 100)
      .set('pageNumber', 1)
      .set('name', name)
      .set('status', status);

    return this.http
      .get<PaymentApiResponse<PaymentApiPage<Currency>>>(`${this.baseUrl}public/currency`, {
        params,
      })
      .pipe(map((res) => res?.data?.content ?? []));
  }

  /**
   * Deeds-only: the CBZ iVeri application id, which the backend returns wrapped
   * in braces (e.g. `{ea2f…}`) — they are stripped here.
   */
  getApplicationId(): Observable<string> {
    return this.http
      .get<PaymentApiResponse<string>>(`${this.baseUrl}public/payment/cbz/application-id`)
      .pipe(map((res) => (res?.data ?? '').replace(/[{}]/g, '')));
  }

  private handleError(error: HttpErrorResponse) {
    return throwError(() => new Error(error.message || 'Server error'));
  }
}
